import isEmpty from "lodash.isempty";
import { CURRENCY_ID } from "../../../constants/common/common";
import { productTypes } from "../../../constants/pageConstants/pdpConstants";
import {
  requestToFetchBlockedDatesAction,
  requestToFetchDeliveryDatesAction,
  resetGetDeliveryDateAndGeoPriceAction,
  setDeliveryDetailsAction,
} from "../../../store/slices/common/shippingDateAndSlotSelection";
import {
  requestToFetchProductStockAvailable,
  requestToUpdateProductDetails,
  setDeliveryType,
  setUserSelectedDeliveryType,
} from "../../../store/slices/pdpSlice";
import { isSupplierFlowEnabled } from "../../api/controlProxy";

/**
 * This method is used to check the product is international or not
 *
 * @returns {boolean}  will return true or false
 */
export const checkIsInternationalProduct = (primaryCategoryId) => {
  return primaryCategoryId === productTypes.INTERNATIONAL;
};

/**
 * This method is used to check that product is same sku or not
 *
 * @returns  {boolean} is same sku product or not .
 */
export const isSameSkuProduct = (assocProductDetails) => !!assocProductDetails;

/**
 * This method is used to check, delivery available for selected SKU and delivery type.
 *
 * @param {*} deliveryDetails
 * @returns
 */
export const checkIsSKUDeliverable = (deliveryDetails) => {
  const dates = deliveryDetails?.deliveryDates;
  if (dates?.length) {
    return deliveryDetails.isDatesAvailable;
  }
  return false;
};

/**
 * This method is used to check product is non deliverable or not for domestic.
 *
 * @param {*} deliveryDetails
 * @param {*} assocProductDetails
 * @param {*} primaryCategoryId
 * @returns {boolean} true or false
 */
export const isNonDeliverable = (deliveryDetails, assocProductDetails) => {
  const isDatesAvailable = deliveryDetails?.isDatesAvailable;
  if (isDatesAvailable === undefined) return false;
  const isNotDeliverable = isSameSkuProduct(assocProductDetails)
    ? !checkIsSKUDeliverable(deliveryDetails)
    : !isDatesAvailable;

  return isNotDeliverable;
};

/**
 * This method is used to check product is non deliverable or not for abroad.
 * @param {*} isShippingDetailsExists
 */
export const isNonDeliverableAbroad = (isShippingDetailsExists = true) => {
  const isNotDeliverable = !isShippingDetailsExists;
  return isNotDeliverable;
};

/**
 * This method is used to get assoc product price for the same sku products
 *
 * @param {string} productType type of product.
 * @param {*} product
 * @returns  {object} it will return price.
 * @returns
 */
export const getAssocProdIdPrice = (productType, product) => {
  let assocInfo;
  const { assocProductDetails } = product;
  if (productType === product.primaryCategoryId) {
    assocInfo = {
      productId: product.productId,
      price: product.productPrice.price,
      listPrice: product.productPrice.listPrice,
      deliveryType: product.primaryCategoryId,
    };
  } else if (assocProductDetails && assocProductDetails.substituteProduct) {
    assocInfo = {
      productId: assocProductDetails.substituteProduct.productId,
      price: assocProductDetails.substituteProduct.productPrice[assocProductDetails.substituteProduct.productId].price,
      listPrice:
        assocProductDetails.substituteProduct.productPrice[assocProductDetails.substituteProduct.productId].listPrice,
      deliveryType: assocProductDetails.substituteProduct.primaryProductCategoryId,
    };
  }
  return assocInfo;
};

/**
 * It will auto-filled the date time slot, if shipping details are available.
 * @param {*} param0
 * @param {*} deliveryDetails
 * @param {*} dispatch
 */
export const onAutoFillDateTimeSlots = ({ data, selectedShippingMethod } = {}, deliveryDetails, dispatch) => {
  if (data?.length === 1 && deliveryDetails?.isDatesAvailable) {
    const earliestShippingDetails = data?.[0]?.[selectedShippingMethod];
    if (!isEmpty(earliestShippingDetails)) {
      const { startTime, endTime } = earliestShippingDetails.timeSlots[0];
      const formatTime = `${startTime} - ${endTime} Hrs`;
      const timeSlotDetails = { timeSlots: earliestShippingDetails?.timeSlots?.[0], formatTime };
      dispatch(
        setDeliveryDetailsAction({
          currentShippingMethod: {
            shippingMethodId: selectedShippingMethod,
            shippingMethodName: earliestShippingDetails?.shippingMethodName,
          },
          timeSlots: timeSlotDetails,
          selectedDate: deliveryDetails?.selectedDeliveryDate,
        }),
      );
    }
  } else {
    dispatch(setDeliveryDetailsAction({}));
    dispatch(resetGetDeliveryDateAndGeoPriceAction());
  }
};

/**
 * Switch Delivery Type
 * @param {*} stockData
 * @param {*} type
 * @param {*} deliveryTypeProductId
 * @param {*} productId
 * @param {*} primaryCategoryId
 * @param {*} catalogId
 * @param {*} dispatch
 * @param {*} countryGeoId
 */
export const switchDeliveryType = (
  stockData,
  type,
  deliveryTypeProductId,
  productId,
  primaryCategoryId,
  catalogId,
  pincode,
  dispatch,
  countryGeoId,
  partyId,
) => {
  if (!stockData?.data?.length) {
    const payload = {
      productId: `${productId},${deliveryTypeProductId}`,
      pinCode: pincode,
    };
    dispatch(requestToFetchProductStockAvailable(payload));
  }
  dispatch(setDeliveryType(type));
  if (type !== primaryCategoryId && deliveryTypeProductId !== productId) {
    dispatch(
      requestToUpdateProductDetails({
        productId: deliveryTypeProductId,
        currencyUomId: CURRENCY_ID,
        catalogId,
      }),
    );
  }
  dispatch(setUserSelectedDeliveryType(type));
  // The delivery-date chips/calendar are otherwise left showing the previously selected
  // fulfillment type's dates, since nothing else re-fetches them for the new productId.
  dispatch(
    requestToFetchDeliveryDatesAction({
      productId: deliveryTypeProductId,
      pinCode: pincode,
      currencyUomId: CURRENCY_ID,
      catalogId,
      countryGeoId,
      partyId: isSupplierFlowEnabled() ? partyId : "",
    }),
  );
  dispatch(
    requestToFetchBlockedDatesAction({
      productId: deliveryTypeProductId,
      pinCode: pincode,
    }),
  );
};

/**
 * Return true if product is out of stock.
 * @param {*} productStockData
 * @param {*} productId
 * @returns
 */
export const getOutOfStock = (productStockData, productId, selectedDate) => {
  const productStockDataAvailable = productStockData?.data || [];
  const filteredItem = productStockDataAvailable?.filter((item) => item.productId === productId);
  if (filteredItem?.[0]?.eventDate) {
    return filteredItem?.[0]?.isProductStockAvailable === "N" && filteredItem?.[0]?.eventDate.includes(selectedDate);
  }
  return filteredItem?.[0]?.isProductStockAvailable === "N";
};
